import { useI18n } from 'vue-i18n'
import { isOffered } from '../prices'
import { formatPrice, formatRate } from '../utils/formatPrice'

export interface UseMoneyReturn {
  /** An amount, in the displayed language, with no tax mention — a balance, a shortfall. */
  price: (cents: number, currency?: string) => string
  /** A price excluding tax, said so: `10,00 € HT`, `€10.00 excl. VAT`. */
  priceExclTax: (cents: number, currency?: string) => string
  /**
   * A unit rate excluding tax, with the decimals it needs (`formatRate`): `0,035 € HT/Go/mois`,
   * `€0.10/minute excl. VAT`. `unit` is glued after the figure — `''` where the sentence around it
   * names the unit itself.
   */
  rateExclTax: (cents: number, unit: string, currency?: string) => string
  /** {@link priceExclTax}, or « Offert » for a price of zero — a fee negotiated away. */
  priceOrOffered: (cents: number, currency?: string) => string
  /** {@link rateExclTax}, or « Offert » for a rate of zero — a service negotiated away. */
  rateOrOffered: (cents: number, unit: string, currency?: string) => string
}

/** A catalog lookup, as `useI18n`'s `t` answers one. */
export type MoneyTranslate = (key: string, named?: Record<string, unknown>) => string

/**
 * The money the app bills, written the one way (`docs/plans/billing-ui.md` U2).
 *
 * **Every price inside the app is excluding tax, and says so**: tariffs, fees, top-up amounts and
 * their bonuses, consumption. The tax-inclusive figure belongs to the payment step and the invoice,
 * which read it from the server. A balance is credits, not a price, and takes `price`.
 *
 * **An amount is written in cents, a rate down to its micro-cent**: what is charged, held or owed
 * is whole cents (`formatPrice`), while a price per unit is the price the organization is quoted,
 * fractional where negotiated so (`formatRate`) — 0,035 € a gigabyte is not 0,04 €.
 *
 * The mention is a catalog string rather than a suffix glued on here, because its place moves with
 * the language — French writes it after the amount and before the unit (`0,10 € HT/minute`),
 * English after the whole rate (`€0.10/minute excl. VAT`). The locale is the component's own, so a
 * language switch reformats every figure.
 */
export function useMoney(): UseMoneyReturn {
  const { t, locale } = useI18n()
  return moneyIn(t, () => locale.value)
}

/**
 * {@link useMoney}'s words, from a catalog lookup and the language displayed — what a spec builds
 * them from without a component.
 */
export function moneyIn(t: MoneyTranslate, locale: () => string): UseMoneyReturn {
  function price(cents: number, currency = 'EUR'): string {
    return formatPrice(cents, currency, locale())
  }

  function priceExclTax(cents: number, currency = 'EUR'): string {
    return t('billing.money.exclTax', { amount: price(cents, currency) })
  }

  function rateExclTax(cents: number, unit: string, currency = 'EUR'): string {
    return t('billing.money.rateExclTax', { amount: formatRate(cents, currency, locale()), unit })
  }

  // A price of zero is a price — what an organization was offered — and « 0,00 € HT » would read
  // as a figure nobody set (`docs/plans/org-pricing.md` OP5). Decided on the figure handed in, never
  // on the one written: a rate of 0,3 c is shown as such, not as nothing.
  function priceOrOffered(cents: number, currency = 'EUR'): string {
    return isOffered(cents) ? t('billing.money.offered') : priceExclTax(cents, currency)
  }

  function rateOrOffered(cents: number, unit: string, currency = 'EUR'): string {
    return isOffered(cents) ? t('billing.money.offered') : rateExclTax(cents, unit, currency)
  }

  return { price, priceExclTax, rateExclTax, priceOrOffered, rateOrOffered }
}
